import Compressor from 'compressorjs';

interface CompressImageOptions {
  maxSizeKB?: number;        // default 500
  maxWidthOrHeight?: number; // default 1920
}

/**
 * Compresses an image file on the client-side before upload.
 * Iteratively reduces quality until the output is under maxSizeKB.
 * @param file The original image file to compress.
 * @param options Configuration for the compression.
 * @returns A promise that resolves with the compressed File object.
 */
export async function compressImage(
  file: File,
  options: CompressImageOptions = {}
): Promise<File> {
  const { maxSizeKB = 500, maxWidthOrHeight = 1920 } = options;

  console.log(`[Compress] Starting compression for ${file.name}. Original size: ${(file.size / 1024).toFixed(0)} KB`);

  async function attempt(quality: number): Promise<File> {
    return new Promise((resolve, reject) => {
      new Compressor(file, {
        quality,
        maxWidth: maxWidthOrHeight,
        maxHeight: maxWidthOrHeight,
        success(result: Blob) {
          const out = new File([result], file.name, { type: result.type, lastModified: Date.now() });
          console.log(`[Compress] quality=${quality.toFixed(2)} → ${(out.size / 1024).toFixed(0)} KB`);
          if (out.size > maxSizeKB * 1024 && quality - 0.15 >= 0.2) {
            resolve(attempt(Math.round((quality - 0.15) * 100) / 100));
          } else {
            console.log(`[Compress] Final size: ${(out.size / 1024).toFixed(0)} KB`);
            resolve(out);
          }
        },
        error(err: Error) {
          console.error('[Compress] Image compression failed:', err);
          reject(new Error('Failed to compress the image. Please try a different file.'));
        },
      });
    });
  }

  return attempt(0.8);
}
